import { markingApi } from '../../api/marking';
import type { GtinMarkItem } from '../../api/marking';
import { DraftLine, INFO_ROW_SUFFIX, isVolumeUnit } from './types';

// Единая логика разливного товара (маркированные бочки, объём в литрах) —
// общая для полного вида (ServiceOrderPage) и вида «Замена масла» (OilChangePage).
// Схема: ServiceOrderPage.md, OilChangePage.md §1 п.4 (rev. 2026-09-08 №2) — логика едина.

// Авто-перераспределение объёма мастер-строки по дочерним маркам (схема 3):
// увеличение — добор в существующие марки с конца (зеркало уменьшению), затем новые по порядку подбора;
// уменьшение — сначала «невозможный» объём сверх потолка марки, затем LIFO-возврат
export const redistributeMaster = (master: DraftLine, newQty: number): DraftLine => {
  const children = (master.children || []).map(c => ({ ...c, manual_volume: false }));
  const current = children.reduce((s, c) => s + c.quantity, 0);
  let need = newQty - current;

  if (need > 0) {
    // Добор в уже добавленные марки — с КОНЦА (зеркало LIFO-уменьшения):
    // уменьшил/увеличил — объём возвращается в ту же марку
    // available_volume дочерней строки — её потолок (уже включает текущее quantity)
    for (let i = children.length - 1; i >= 0 && need > 0; i--) {
      const child = children[i];
      const usable = child.available_volume ?? child.quantity;
      const add = Math.min(need, Math.max(0, usable - child.quantity));
      if (add > 0) {
        child.quantity = Number((child.quantity + add).toFixed(1));
        need = Number((need - add).toFixed(1));
      }
    }
    // Недостающее — новые марки по порядку подбора (available_marks уже отсортированы сервером)
    if (need > 0 && master.available_marks) {
      const inDoc = new Set(children.map(c => c.marking_code));
      for (const m of master.available_marks) {
        if (need <= 0) break;
        if (!m.selectable || inDoc.has(m.marking_code)) continue;
        const avail = Number(m.available_volume ?? 0);
        const take = Math.min(need, avail);
        if (take <= 0) continue;
        children.push({
          key: `${master.key}:${m.marking_code}`,
          external_1c_id: master.external_1c_id,
          name: master.name,
          quantity: Number(take.toFixed(1)),
          unit: master.unit,
          price: master.price,
          marking_code: m.marking_code,
          mark_has_full_code: m.has_full_code ?? null,
          parent_key: master.key,
          available_volume: avail,
          manual_volume: false,
        });
        inDoc.add(m.marking_code);
        need = Number((need - take).toFixed(1));
      }
    }
  } else if (need < 0) {
    let toReturn = -need;
    // 1) «Невозможный» объём (превышение потолка марки) убирается первым —
    //    иначе уменьшение съедает честный объём других марок, а завышенный остаётся
    for (let i = children.length - 1; i >= 0 && toReturn > 0; i--) {
      const c = children[i];
      if (c.available_volume == null) continue;
      const over = Number(Math.max(0, c.quantity - c.available_volume).toFixed(1));
      if (over <= 0) continue;
      const take = Math.min(toReturn, over);
      const left = Number((c.quantity - take).toFixed(1));
      toReturn = Number((toReturn - take).toFixed(1));
      if (left <= 0) {
        children.splice(i, 1);
      } else {
        c.quantity = left;
      }
    }
    // 2) LIFO-возврат (последние марки уменьшаются/удаляются первыми)
    for (let i = children.length - 1; i >= 0 && toReturn > 0; i--) {
      const take = Math.min(toReturn, children[i].quantity);
      const left = Number((children[i].quantity - take).toFixed(1));
      toReturn = Number((toReturn - take).toFixed(1));
      if (left <= 0) {
        children.splice(i, 1);
      } else {
        children[i].quantity = left;
      }
    }
  }
  return { ...master, quantity: newQty, children };
};

// Потолок объёма мастер-строки = сумма available_volume всех марок товара.
// available марок этого ЗН уже включает взятый ими объём — children отдельно не прибавляем
// (иначе двойной счёт). Дочерние марки, которых нет в списке (стали недоступны), — по их потолку.
export const bulkMasterMaxQty = (l: DraftLine): number => {
  const marksList = l.available_marks || [];
  const inList = new Set(marksList.map(m => m.marking_code));
  return (
    marksList.reduce((s, m) => s + Number(m.available_volume ?? 0), 0) +
    (l.children || [])
      .filter(c => !c.marking_code || !inList.has(c.marking_code))
      .reduce((s, c) => s + (c.available_volume ?? c.quantity), 0)
  );
};

// Валидация перед сохранением: объём мастер-строки должен быть покрыт марками
export const bulkCoverageError = (parts: DraftLine[], isEmpty: (l: DraftLine) => boolean): DraftLine | null =>
  parts.find(
    l => l.is_bulk_master && !isEmpty(l) && l.quantity > 0 &&
      (l.children || []).reduce((s, c) => s + c.quantity, 0) < l.quantity
  ) ?? null;

// Применение загруженных марок к мастер-строке: проставляет свободный остаток
// существующим дочерним маркам. available_volume — потолок дочерней строки:
// с учётом контекста ЗН включает объём, уже взятый этим документом (rev. 2026-08-12)
export const applyMarksToMaster = (parts: DraftLine[], key: string, marks: GtinMarkItem[]): DraftLine[] => {
  const gtin = marks.length > 0 ? marks[0].marking_code.slice(2, 16) : null;
  return parts.map(l => {
    if (l.key !== key) return l;
    const byCode = new Map(marks.map(m => [m.marking_code, m]));
    const children = (l.children || []).map(c => {
      const m = c.marking_code ? byCode.get(c.marking_code) : undefined;
      return m
        ? {
            ...c,
            available_volume: m.available_volume != null ? Number(m.available_volume) : null,
            mark_has_full_code: m.has_full_code ?? c.mark_has_full_code ?? null,
          }
        : c;
    });
    return { ...l, available_marks: marks, bulk_gtin: l.bulk_gtin ?? gtin, children };
  });
};

// Загрузка марок разливного товара (rev. 2026-08-10: вместо несуществующего /1c/mark-balance).
// serviceOrderId — контекст документа: available_volume марок этого ЗН включает взятый им объём
export const fetchProductMarks = async (
  productId: string, serviceOrderId?: number,
): Promise<GtinMarkItem[]> => {
  const { data } = await markingApi.getMarksByProduct(productId, undefined, serviceOrderId);
  return data;
};

// Строгая загрузка «только свои марки товара» — для авто-восстановления мастера
// разлива (rev. 2026-09-30, ServiceOrderPage.md §2В №20). С rev. 2026-10-21
// fallback «все bulk-марки» удалён из API совсем — strict оставлен для совместимости,
// оба варианта возвращают только свои марки товара.
export const fetchOwnProductMarks = async (
  productId: string, serviceOrderId?: number,
): Promise<GtinMarkItem[]> => {
  const { data } = await markingApi.getMarksByProduct(productId, undefined, serviceOrderId, true);
  return data;
};

// Кандидаты на восстановление: плоская литровая позиция без марки (мастер разлива,
// сохранённый без марок как предварительный документ, при round-trip терял флаг
// is_bulk_master — groupPartsToDraft распознаёт мастер только по marking_code)
export const findRestorableBulkLines = (parts: DraftLine[]): DraftLine[] =>
  parts.filter(l =>
    !l.parent_key && !l.is_bulk_master && !l.is_piece_master &&
    !l.marking_code && !!l.external_1c_id && isVolumeUnit(l.unit) &&
    !l.key.endsWith(INFO_ROW_SUFFIX));

// Восстановление мастера разлива из плоской литровой строки (rev. 2026-09-30):
// марки товара есть → строка снова мастер, текущий объём авто-распределяется по бочкам;
// марок нет (товар не маркируется разливом) → строка остаётся плоской
export const promoteToBulkMaster = (
  parts: DraftLine[], key: string, marks: GtinMarkItem[],
): DraftLine[] => {
  if (marks.length === 0) return parts;
  return parts.map(l => {
    if (l.key !== key || l.is_bulk_master || l.marking_code || l.parent_key) return l;
    const master: DraftLine = {
      ...l,
      is_bulk_master: true,
      children: [],
      available_marks: marks,
      bulk_gtin: l.bulk_gtin ?? marks[0].marking_code.slice(2, 16),
    };
    return redistributeMaster(master, l.quantity);
  });
};
